/**
 * SettingsPage — tenant settings for staff admins.
 *
 * Allows configuring:
 *   - Practice display name (shown to patients)
 *   - GHL workflow ID for staff notifications
 *   - Default form AI mode
 *   - Default magic link expiry (hours)
 *   - jsreport URL override (advanced)
 *   - Disconnect GHL (with confirmation)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { settings as settingsApi, TenantSettings } from '../../../lib/api.js';
import DataMigrationCard from './DataMigrationCard.js';

const AI_MODES: Array<{ value: TenantSettings['defaultAiMode']; label: string }> = [
  { value: 'ai_only',   label: 'AI Only — patients complete forms via chat' },
  { value: 'toggle',    label: 'Toggle — patients can switch between AI and form' },
  { value: 'form_only', label: 'Form Only — traditional form, no AI' },
];

const EXPIRY_OPTIONS = [1, 2, 4, 8, 24, 48, 72, 168];

export default function SettingsPage(): React.ReactElement {
  const [form,        setForm]        = useState<TenantSettings>({
    practiceName:          '',
    ghlWorkflowId:         '',
    defaultAiMode:         'ai_only',
    defaultMagicLinkExpiry: 24,
    jsreportUrlOverride:   '',
    messageReceivedWebhookUrl: '',
    singleUseLinks:        true,
    aiChatEnabled:         true,
  });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const [disconnecting,     setDisconnecting]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await settingsApi.get();
      if (res.success && res.data) {
        setForm(res.data);
      } else {
        setError(res.error ?? 'Failed to load settings');
      }
    } catch {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleChange = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await settingsApi.update(form);
      if (res.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(res.error ?? 'Failed to save settings');
      }
    } catch {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setDisconnecting(true);
    try {
      await settingsApi.disconnect();
      // Redirect to a "disconnected" state — refresh will show the auth gate
      window.location.reload();
    } catch {
      setError('Failed to disconnect. Please try again.');
      setDisconnecting(false);
      setDisconnectConfirm(false);
    }
  };

  if (loading) {
    return <div style={styles.center}><p style={styles.muted}>Loading settings…</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Settings</h1>
        <p style={styles.pageSubtitle}>Configure ClearIntake for your practice</p>
      </div>

      <form onSubmit={(e) => void handleSave(e)} style={styles.form}>
        {/* Practice */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Practice</h2>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="practiceName">
              Practice display name
            </label>
            <p style={styles.helpText}>Shown to patients during AI intake and on PDFs.</p>
            <input
              id="practiceName"
              type="text"
              style={styles.input}
              value={form.practiceName}
              onChange={(e) => handleChange('practiceName', e.target.value)}
              placeholder="e.g. Sunrise Family Medicine"
              maxLength={255}
            />
          </div>
        </div>

        {/* Forms defaults */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Form Defaults</h2>

          <div style={styles.field}>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.aiChatEnabled}
                onChange={(e) => handleChange('aiChatEnabled', e.target.checked)}
              />
              <span>Enable AI chat for forms</span>
            </label>
            <p style={styles.helpText}>
              When off, patients always fill forms manually — the AI chat option is hidden
              on every form, regardless of each form&apos;s AI mode.
            </p>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="defaultAiMode">Default AI mode</label>
            <p style={styles.helpText}>Applied to new forms unless overridden per-form.</p>
            <select
              id="defaultAiMode"
              style={styles.select}
              value={form.defaultAiMode}
              onChange={(e) => handleChange('defaultAiMode', e.target.value as TenantSettings['defaultAiMode'])}
            >
              {AI_MODES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="defaultExpiry">Magic link expiry</label>
            <p style={styles.helpText}>How long a patient magic link remains valid.</p>
            <select
              id="defaultExpiry"
              style={styles.select}
              value={form.defaultMagicLinkExpiry}
              onChange={(e) => handleChange('defaultMagicLinkExpiry', parseInt(e.target.value, 10))}
            >
              {EXPIRY_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {h < 24 ? `${h} hour${h > 1 ? 's' : ''}` : `${h / 24} day${h / 24 > 1 ? 's' : ''}`}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={form.singleUseLinks}
                onChange={(e) => handleChange('singleUseLinks', e.target.checked)}
              />
              <span>Single-use links</span>
            </label>
            <p style={styles.helpText}>
              Recommended. A magic link stops working after the patient verifies once.
              Turn off to let a link be reopened until it expires.
            </p>
          </div>
        </div>

        {/* Automations */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Automations</h2>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="ghlWorkflowId">
              Staff notification workflow ID
            </label>
            <p style={styles.helpText}>
              Automation workflow to trigger when a new form submission arrives.
              Leave blank to disable automatic notifications.
            </p>
            <input
              id="ghlWorkflowId"
              type="text"
              style={styles.input}
              value={form.ghlWorkflowId}
              onChange={(e) => handleChange('ghlWorkflowId', e.target.value)}
              placeholder="e.g. abc123-def456-..."
              maxLength={255}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="messageReceivedWebhookUrl">
              Secure message received — workflow webhook URL
            </label>
            <p style={styles.helpText}>
              Fire your own automation workflow whenever a patient replies to a secure message.
              In your automation platform, build a workflow, add the <strong>Inbound Webhook</strong> trigger,
              copy its URL, and paste it here. ClearIntake calls it with IDs only
              (no message content) — your workflow can notify staff, create a task, and more.
              Leave blank to disable.
            </p>
            <input
              id="messageReceivedWebhookUrl"
              type="url"
              style={styles.input}
              value={form.messageReceivedWebhookUrl}
              onChange={(e) => handleChange('messageReceivedWebhookUrl', e.target.value)}
              placeholder="https://services.leadconnectorhq.com/hooks/..."
              maxLength={1000}
            />
          </div>
        </div>

        {/* Advanced */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Advanced</h2>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="jsreportUrl">jsreport URL override</label>
            <p style={styles.helpText}>
              Override the jsreport instance URL (defaults to the <code>JSREPORT_URL</code> environment variable).
              Only change this if your jsreport runs at a non-standard location.
            </p>
            <input
              id="jsreportUrl"
              type="url"
              style={styles.input}
              value={form.jsreportUrlOverride}
              onChange={(e) => handleChange('jsreportUrlOverride', e.target.value)}
              placeholder="e.g. http://localhost:5488"
              maxLength={500}
            />
          </div>
        </div>

        {/* Save bar */}
        {error && <div style={styles.errorBanner}>{error}</div>}
        {saved  && <div style={styles.successBanner}>✅ Settings saved</div>}

        <div style={styles.saveBar}>
          <button
            type="submit"
            style={{ ...styles.primaryBtn, opacity: saving ? 0.6 : 1 }}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Data migration */}
      <div style={{ marginTop: 20 }}>
        <DataMigrationCard />
      </div>

      {/* Danger zone */}
      <div style={{ ...styles.card, borderColor: '#fca5a5' }}>
        <h2 style={{ ...styles.cardTitle, color: '#b91c1c' }}>Danger Zone</h2>
        <p style={{ fontSize: 14, color: '#374151', marginBottom: 12 }}>
          Disconnecting removes this account's connection. Staff will not be able to
          log in until the app is re-installed.
        </p>

        {disconnectConfirm ? (
          <div style={styles.confirmBox}>
            <p style={{ fontSize: 14, color: '#b91c1c', fontWeight: 600, marginBottom: 12 }}>
              Are you sure? This will log out all staff immediately.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ ...styles.dangerBtn, opacity: disconnecting ? 0.6 : 1 }}
                disabled={disconnecting}
                onClick={() => void handleDisconnect()}
              >
                {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
              </button>
              <button
                style={styles.cancelBtn}
                disabled={disconnecting}
                onClick={() => setDisconnectConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button style={styles.dangerBtn} onClick={() => setDisconnectConfirm(true)}>
            Disconnect
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth:  640,
    margin:    '0 auto',
    padding:   '24px 20px 48px',
    overflowY: 'auto',
    height:    '100%',
    boxSizing: 'border-box',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  center: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
  },
  muted: { color: '#9ca3af', fontSize: 14 },
  pageHeader: { marginBottom: 24 },
  pageTitle:  { margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#111827' },
  pageSubtitle: { margin: 0, fontSize: 14, color: '#6b7280' },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  card: {
    background:   '#fff',
    border:       '1px solid #e5e7eb',
    borderRadius: 10,
    padding:      '20px 24px',
  },
  cardTitle: { margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#111827' },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' },
  helpText: { fontSize: 12, color: '#9ca3af', margin: '0 0 6px' },
  input: {
    width:        '100%',
    border:       '1px solid #d1d5db',
    borderRadius: 6,
    padding:      '8px 10px',
    fontSize:     14,
    color:        '#111827',
    boxSizing:    'border-box',
  },
  select: {
    width:        '100%',
    border:       '1px solid #d1d5db',
    borderRadius: 6,
    padding:      '8px 10px',
    fontSize:     14,
    color:        '#111827',
    background:   '#fff',
    boxSizing:    'border-box',
  },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 16px', color: '#b91c1c', fontSize: 13,
  },
  successBanner: {
    background: '#f0fdf4', border: '1px solid #86efac',
    borderRadius: 6, padding: '10px 16px', color: '#166534', fontSize: 13,
  },
  saveBar: { display: 'flex', justifyContent: 'flex-end' },
  primaryBtn: {
    background: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 7, padding: '10px 24px', cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  dangerBtn: {
    background: '#fef2f2', color: '#b91c1c',
    border: '1px solid #fca5a5', borderRadius: 7,
    padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  cancelBtn: {
    background: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: 7,
    padding: '8px 16px', cursor: 'pointer', fontSize: 13,
  },
  confirmBox: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 8, padding: '16px',
  },
};
